import { useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Building,
  Building2,
  ChevronsUpDown,
  Check,
  Loader2,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { ALL_LOCATIONS_LABEL, supportsAllLocations } from '@/lib/allLocations';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';

const MAX_VISIBLE_ITEMS = 50;

interface ClinicSelectorProps {
  isCollapsed: boolean;
}

export function ClinicSelector({ isCollapsed }: ClinicSelectorProps) {
  const {
    accessibleClinics,
    activeClinicId,
    activeClinicName,
    setActiveClinicId,
    canViewAllLocations,
    allLocationsActive,
    setAllLocationsActive,
    isLoading,
  } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectionLabel = allLocationsActive
    ? ALL_LOCATIONS_LABEL
    : activeClinicName;

  const filteredClinics = useMemo(() => {
    if (!search.trim()) return accessibleClinics.slice(0, MAX_VISIBLE_ITEMS);
    const query = search.toLowerCase();
    const matches = [];
    for (const clinic of accessibleClinics) {
      if (
        clinic.name?.toLowerCase().includes(query) ||
        clinic.id.toLowerCase().includes(query)
      ) {
        matches.push(clinic);
        if (matches.length >= MAX_VISIBLE_ITEMS) break;
      }
    }
    return matches;
  }, [accessibleClinics, search]);

  const totalMatches = useMemo(() => {
    if (!search.trim()) return accessibleClinics.length;
    const query = search.toLowerCase();
    return accessibleClinics.filter(
      c =>
        c.name?.toLowerCase().includes(query) ||
        c.id.toLowerCase().includes(query)
    ).length;
  }, [accessibleClinics, search]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) setSearch('');
  }, []);

  const handleSelectClinic = (clinicId: string) => {
    setActiveClinicId(clinicId);
    setAllLocationsActive(false);
    setOpen(false); // Close popover on selection
  };

  const handleSelectAllLocations = () => {
    setOpen(false);
    setAllLocationsActive(true);
    if (!supportsAllLocations(pathname)) navigate('/referrals');
  };

  if (accessibleClinics.length < 2) {
    return null;
  }

  // --- Loading State ---
  if (isLoading && accessibleClinics.length === 0) {
    // Show loading only initially
    return (
      <div
        className={cn(
          'px-4 py-2 text-sm text-muted-foreground flex items-center',
          isCollapsed ? 'justify-center' : ''
        )}
      >
        {isCollapsed ? (
          <Loader2 className="h-5 w-5 mx-auto animate-spin" />
        ) : (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span>Loading clinics...</span>
          </>
        )}
      </div>
    );
  }

  // --- No Clinics State ---
  if (!isLoading && accessibleClinics.length === 0) {
    return (
      <div
        className={cn(
          'px-4 py-2 text-sm text-muted-foreground',
          isCollapsed ? 'text-center' : ''
        )}
      >
        {isCollapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Building className="h-5 w-5 mx-auto opacity-50" />
              </TooltipTrigger>
              <TooltipContent side="right">No clinics assigned</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          'No clinics assigned'
        )}
      </div>
    );
  }

  // --- Collapsed View ---
  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="w-full justify-center my-1 h-9"
              onClick={() => setOpen(true)}
            >
              <Building className="h-5 w-5" />
              <span className="sr-only">Select Clinic</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {selectionLabel ? `Active: ${selectionLabel}` : 'Select Clinic'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // --- Expanded View ---
  return (
    <div className="w-full">
      {' '}
      {/* Ensure full width */}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-9 overflow-hidden"
            aria-label="Select clinic"
          >
            <Building className="mr-2 h-4 w-4 shrink-0 opacity-70 flex-none" />
            <span className="truncate flex-1 text-left font-normal text-sm">
              {selectionLabel ?? 'Select clinic...'}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by name or ID..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No clinic found.</CommandEmpty>
              <CommandGroup>
                {canViewAllLocations && !search.trim() && (
                  <CommandItem
                    value="all-locations"
                    onSelect={handleSelectAllLocations}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        allLocationsActive ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <Building2 className="mr-2 h-4 w-4 opacity-70" />
                    <div className="flex flex-col">
                      <span>{ALL_LOCATIONS_LABEL}</span>
                      <span className="text-caption-long-secondary">
                        {accessibleClinics.length} locations
                      </span>
                    </div>
                  </CommandItem>
                )}
                {filteredClinics.map(clinic => (
                  <CommandItem
                    key={clinic.id}
                    value={clinic.id}
                    onSelect={() => {
                      handleSelectClinic(clinic.id);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        !allLocationsActive && activeClinicId === clinic.id
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{clinic.name}</span>
                      <span className="text-caption-long-secondary">
                        {clinic.id}
                      </span>
                    </div>
                  </CommandItem>
                ))}
                {totalMatches > MAX_VISIBLE_ITEMS && (
                  <p className="py-2 text-center text-caption-long-secondary">
                    Showing {MAX_VISIBLE_ITEMS} of {totalMatches} results
                  </p>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
